import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin, Building2, Users, Layers, Award, Check,
  Quote, Circle, Activity, Package, Armchair, Car,
  ShoppingCart, Wifi, Coffee, Heart, Volume2, Monitor,
  Lightbulb, Wind, MessageSquare, Phone, Calendar,
  ChevronDown,
  Info, Star, Bus, Plane, Clock, DollarSign
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { sofiaMladostTranslations } from '../translations/sofiaMladost';
import { useManagedContent } from '../hooks/useManagedContent';
import { useManagedImages } from '../hooks/useManagedImages';
import { pageImageConfig } from '../content/pageImageConfig';
import '../App.css';

function SofiaMladost() {
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'sofia-mladost',
    language,
    defaultTranslations: sofiaMladostTranslations
  });
  const images = useManagedImages({
    pageKey: 'sofia-mladost',
    defaultImages: pageImageConfig['sofia-mladost']
  });
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [expandedSport, setExpandedSport] = useState(null);

  const similarImages = [
    "https://i.postimg.cc/YSkxGSMS/Chat-GPT-Image-Jan-20-2026-01-43-18-PM.png",
    "https://i.postimg.cc/YCQ3CFcd/Chat-GPT-Image-Jan-20-2026-01-45-16-PM.png",
    "https://i.postimg.cc/W10n1ZRH/Chat-GPT-Image-Jan-20-2026-01-45-12-PM.png",
  ];
  const testimonials = t.testimonials.items.map((x) => ({ ...x, rating: 5 }));
  const faqs = t.faq.items;
  const similarLocations = t.similar.locations.map((loc, i) => ({ ...loc, image: similarImages[i] }));
  const overviewCards = [
    { icon: Building2, ...t.overview.cards[0] },
    { icon: Users, ...t.overview.cards[1] },
    { icon: Layers, ...t.overview.cards[2] },
    { icon: Award, ...t.overview.cards[3] },
  ];
  const amenitiesIcons = [Users, Package, Armchair, Car, ShoppingCart, Wifi, Coffee, Heart, Volume2, Monitor, Lightbulb, Wind];
  const amenitiesList = t.amenities.items.map((item, i) => ({ ...item, icon: amenitiesIcons[i] }));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App">
      <Header />

      {/* Hero Section */}
      <section className="location-detail-hero">
        <div className="location-hero-background">
          <div className="location-hero-fallback">
            <img 
              src={images.hero}
              alt="Sports Hall Levski Sofia"
              loading="eager"
            />
          </div>
          <div className="location-hero-overlay"></div>
        </div>

        <div className="location-hero-content">
          <nav className="breadcrumb location-breadcrumb">
            <Link to="/">{t.breadcrumb.home}</Link>
            <span> / </span>
            <Link to="/locations">{t.breadcrumb.locations}</Link>
            <span> / </span>
            <span>{t.breadcrumb.mladost}</span>
          </nav>

          <div className="location-badge-hero">
            <MapPin size={16} />
            <span>{t.hero.badge}</span>
          </div>

          <motion.h1 
            className="location-hero-heading"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t.hero.heading}
          </motion.h1>

          <motion.p 
            className="location-hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t.hero.subheading}
          </motion.p>

          <motion.div 
            className="location-sport-tags"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <span className="sport-tag-hero">
              <Circle size={18} />
              {t.hero.basketball}
            </span>
            <span className="sport-tag-hero">
              <Activity size={18} />
              {t.hero.volleyball}
            </span>
          </motion.div>

          <motion.div 
            className="location-quick-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="quick-info-item">
              <MapPin size={18} />
              <span>{t.hero.district}</span>
            </div>
            <div className="quick-info-item">
              <Users size={18} />
              <span>{t.hero.spectators}</span>
            </div>
            <div className="quick-info-item">
              <Circle size={18} />
              <span>{t.hero.sportsList}</span>
            </div>
            <div className="quick-info-item">
              <Star size={18} />
              <span>{t.hero.stars}</span>
            </div>
          </motion.div>

          <motion.div 
            className="location-hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Link to="/book" className="btn-primary btn-hero-primary">
              {t.hero.bookFacility}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Quick Overview Cards */}
      <section className="location-overview-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.overview.heading}
          </motion.h2>
          <div className="overview-cards-grid">
            {overviewCards.map((card, index) => {
              const IconComponent = card.icon;
              return (
                <motion.div
                  key={index}
                  className="overview-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="overview-card-icon">
                    <IconComponent size={40} color="#C49818" />
                  </div>
                  <h3>{card.heading}</h3>
                  <p className="overview-card-content">{card.content}</p>
                  <p className="overview-card-subtext">{card.subtext}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About the Facility */}
      <section className="location-about-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.about.heading}
          </motion.h2>
          <div className="location-about-content">
            <motion.div 
              className="location-about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="location-about-description">
                {t.about.description}
              </p>
              
              <div className="location-highlights">
                <h3>{t.about.keyHighlights}</h3>
                <ul>
                  {t.about.highlights.map((h, i) => (
                    <li key={i}><Check size={20} color="#C49818" /> {h}</li>
                  ))}
                </ul>
              </div>

              <div className="location-testimonial-box">
                <Quote size={32} color="#C49818" />
                <p className="testimonial-text">
                  {t.about.testimonial}
                </p>
                <p className="testimonial-attribution">{t.about.testimonialAttribution}</p>
              </div>
            </motion.div>

            <motion.div 
              className="location-about-gallery"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="location-gallery-preview">
                <img 
                  src={images.about}
                  alt="Sports Hall Levski Sofia"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sports & Activities */}
      <section className="location-sports-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.sports.heading}
          </motion.h2>
          <div className="sports-cards-grid">
            <motion.div
              className="sport-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -5 }}
            >
              <div className="sport-card-icon">
                <Circle size={56} color="#C49818" />
              </div>
              <h3>{t.sports.basketball}</h3>
              <p className="sport-card-description">
                {t.sports.basketballDesc}
              </p>
              <button 
                className="sport-expand-btn"
                onClick={() => setExpandedSport(expandedSport === 'basketball' ? null : 'basketball')}
              >
                {expandedSport === 'basketball' ? t.sports.hideSpecs : t.sports.viewSpecs}
                <ChevronDown size={18} className={expandedSport === 'basketball' ? 'expanded' : ''} />
              </button>
              {expandedSport === 'basketball' && (
                <motion.div
                  className="sport-specifications"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="spec-section">
                    <h4>{t.sports.courtSpecs}</h4>
                    <ul>
                      {t.sports.basketballSpecsList.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                  <div className="spec-section">
                    <h4>{t.sports.idealFor}</h4>
                    <ul>
                      {t.sports.basketballIdealFor.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                </motion.div>
              )}
            </motion.div>

            <motion.div
              className="sport-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -5 }}
            >
              <div className="sport-card-icon">
                <Activity size={56} color="#C49818" />
              </div>
              <h3>{t.sports.volleyball}</h3>
              <p className="sport-card-description">
                {t.sports.volleyballDesc}
              </p>
              <button 
                className="sport-expand-btn"
                onClick={() => setExpandedSport(expandedSport === 'volleyball' ? null : 'volleyball')}
              >
                {expandedSport === 'volleyball' ? t.sports.hideSpecs : t.sports.viewSpecs}
                <ChevronDown size={18} className={expandedSport === 'volleyball' ? 'expanded' : ''} />
              </button>
              {expandedSport === 'volleyball' && (
                <motion.div
                  className="sport-specifications"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="spec-section">
                    <h4>{t.sports.courtSpecs}</h4>
                    <ul>
                      {t.sports.volleyballSpecsList.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                  <div className="spec-section">
                    <h4>{t.sports.idealFor}</h4>
                    <ul>
                      {t.sports.volleyballIdealFor.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facilities & Amenities */}
      <section className="location-amenities-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.amenities.heading}
          </motion.h2>
          <div className="amenities-grid">
            {amenitiesList.map((amenity, index) => {
              const IconComponent = amenity.icon;
              return (
                <motion.div
                  key={index}
                  className="amenity-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="amenity-icon">
                    <IconComponent size={32} color="#C49818" />
                  </div>
                  <h4>{amenity.title}</h4>
                  <p>{amenity.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Gallery */}
      <section className="location-gallery-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.gallery.heading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.gallery.subheading}
          </motion.p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <motion.div
              className="youtube-video-container"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '177.78%', // 9:16 aspect ratio for vertical videos
                height: 0,
                overflow: 'hidden',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/yirmS757NQw"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                  borderRadius: '12px'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Sports Hall Levski Sofia Facility Video 1"
              ></iframe>
            </motion.div>
            <motion.div
              className="youtube-video-container"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '177.78%', // 9:16 aspect ratio for vertical videos
                height: 0,
                overflow: 'hidden',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/MxgaRmrvaWM"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                  borderRadius: '12px'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Sports Hall Levski Sofia Facility Video 2"
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facility Gallery Images */}
      <section className="location-gallery-images-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.galleryImages.heading}
          </motion.h2>
          <div className="gallery-images-grid">
            {[
              "https://i.postimg.cc/SxxHRVgD/Levski-Sofia-(1).webp",
              "https://i.postimg.cc/g221j4g7/view-1-1614579579.webp",
              "https://i.postimg.cc/t448JDrQ/yanuari-2021-2-1614579679.webp",
              "https://i.postimg.cc/650DBFfg/zala-view-1614579588-(1).webp"
            ].map((img, index) => (
              <motion.div
                key={index}
                className="gallery-image-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, zIndex: 10 }}
              >
                <img src={img} alt={`Facility image ${index + 1}`} loading="lazy" />
                <div className="gallery-image-overlay"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Access */}
      <section className="location-access-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.access.heading}
          </motion.h2>
          <div className="location-access-content">
            <motion.div 
              className="location-map-container"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="location-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2933.5!2d23.3!3d42.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDM5JzAwLjAiTiAyM8KwMTgnMDAuMCJF!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sports Hall Levski Sofia Location"
                ></iframe>
              </div>
            </motion.div>

            <motion.div 
              className="location-access-info"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="access-address-card">
                <MapPin size={24} color="#C49818" />
                <div>
                  <h3>{t.access.address}</h3>
                  <p>{t.access.addressLine1}</p>
                  <p>{t.access.addressLine2}</p>
                  <p>{t.access.addressLine3}</p>
                </div>
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=Sports+Hall+Levski+Sofia,+Mladost+District,+Sofia,+Bulgaria"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary btn-directions"
                >
                  {t.access.getDirections}
                </a>
              </div>

              <div className="access-info-item">
                <Car size={24} color="#C49818" />
                <div>
                  <h4>{t.access.byCar}</h4>
                  <p>{t.access.car1}</p>
                  <p>{t.access.car2}</p>
                  <p>{t.access.car3}</p>
                </div>
              </div>

              <div className="access-info-item">
                <Bus size={24} color="#C49818" />
                <div>
                  <h4>{t.access.byBus}</h4>
                  <p>{t.access.bus1}</p>
                  <p>{t.access.bus2}</p>
                  <p>{t.access.bus3}</p>
                </div>
              </div>

              <div className="access-info-item">
                <Plane size={24} color="#C49818" />
                <div>
                  <h4>{t.access.fromAirport}</h4>
                  <p>{t.access.airport1}</p>
                  <p>{t.access.airport2}</p>
                  <p>{t.access.airport3}</p>
                </div>
              </div>

              <div className="access-info-item">
                <MapPin size={24} color="#C49818" />
                <div>
                  <h4>{t.access.nearbyLandmarks}</h4>
                  <p>{t.access.nearby1}</p>
                  <p>{t.access.nearby2}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing & Booking */}
      <section className="location-pricing-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.pricing.heading}
          </motion.h2>
          <motion.div 
            className="pricing-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="pricing-icon">
              <DollarSign size={48} color="#C49818" />
            </div>
            <h3>{t.pricing.flexiblePricing}</h3>
            <p className="pricing-description">
              {t.pricing.pricingVaries}
            </p>
            <ul className="pricing-factors">
              {t.pricing.factors.map((f, i) => <li key={i}>{f}</li>)}
            </ul>

            <div className="pricing-note-box">
              <Info size={24} color="#C49818" />
              <p>{t.pricing.pricingNote}</p>
            </div>

            <div className="pricing-includes">
              <h4>{t.pricing.whatsIncluded}</h4>
              <ul>
                {t.pricing.included.map((x, i) => (
                  <li key={i}><Check size={18} color="#C49818" /> {x}</li>
                ))}
              </ul>
            </div>

            <div className="pricing-addons">
              <h4>{t.pricing.optionalAddons}</h4>
              <ul>
                {t.pricing.addons.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>

            <div className="pricing-cta-buttons">
              <Link to="/book" className="btn-primary btn-pricing-primary">
                {t.pricing.requestQuote}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="location-testimonials-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.testimonials.heading}
          </motion.h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="testimonial-quote-icon">
                  <Quote size={32} color="#C49818" />
                </div>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} color="#C49818" fill="#C49818" />
                  ))}
                </div>
                <p className="testimonial-quote">{testimonial.quote}</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="testimonial-name">{testimonial.name}</p>
                    <p className="testimonial-date">{testimonial.date}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Similar Locations */}
      <section className="location-similar-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.similar.heading}
          </motion.h2>
          <div className="similar-locations-grid">
            {similarLocations.map((location, index) => (
              <motion.div
                key={index}
                className="similar-location-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="similar-location-image">
                  <img src={location.image} alt={location.name} loading="lazy" />
                  <span className="similar-location-badge">{location.badge}</span>
                </div>
                <div className="similar-location-content">
                  <h3>{location.name}</h3>
                  <p className="similar-location-location">
                    <MapPin size={16} />
                    {location.location}
                  </p>
                  <p className="similar-location-description">{location.description}</p>
                  <Link to={location.path || '#'}>
                    <button className="btn-secondary btn-similar-location">
                      {t.similar.viewDetails}
                    </button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="location-faq-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.faq.heading}
          </motion.h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <button
                  className="faq-question"
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown 
                    size={20} 
                    color="#C49818"
                    className={expandedFAQ === index ? 'faq-expanded' : ''}
                  />
                </button>
                {expandedFAQ === index && (
                  <motion.div
                    className="faq-answer"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <p>{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="location-final-cta">
        <div className="container">
          <motion.div
            className="final-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{t.cta.heading}</h2>
            <p>{t.cta.subheading}</p>
            <div className="final-cta-options">
              <div className="cta-option">
                <MessageSquare size={32} color="#C49818" />
                <p>{t.cta.sendInquiry}</p>
                <Link to="/book" className="btn-primary">{t.cta.requestInfo}</Link>
              </div>
              <div className="cta-option">
                <Phone size={32} color="#C49818" />
                <p>{t.cta.speakWithTeam}</p>
                <p className="cta-phone">+359 88 539 7102</p>
                <button className="btn-secondary">{t.cta.ctaCallNow}</button>
              </div>
              <div className="cta-option">
                <Calendar size={32} color="#C49818" />
                <p>{t.cta.getCompletePackage}</p>
                <Link to="/book" className="btn-primary">{t.cta.bookCompletePackage}</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default SofiaMladost;
